import { useCallback, useEffect, useMemo, useState } from 'react';
import { CardInformation } from 'src/features/paymentMethods/presentation/components/PaymentCardsList/PaymentCardsList.types';
import { useCartContext } from 'src/context/CartProvider/useCartContext';
import useAuthContext from 'src/context/AuthProvider/useAuthContext';
import {
  DeliveryMethodInfo,
  PaymentMethod,
  PaymentMethodSlug,
} from 'src/types/common';
import useCreditCards from 'src/features/creditCards/presentation/hooks/useCreditCards';
import usePaymentMethods from 'src/features/paymentMethods/presentation/hooks/usePaymentMethods';
import calculateDiscount from 'src/utils/coupons/calculateDiscount';
import useStoreSlug from 'src/common/hooks/useStoreSlug';
import { useCheckoutActions } from 'src/context/CheckoutProvider/useCheckoutActions';
import {
  CheckoutContext,
  checkoutInitialState,
} from 'src/context/CheckoutProvider/useCheckoutContext';
import useAvailableCredits from 'src/features/pariWallet/presentation/hooks/useAvailableCredits';

export const CheckoutProvider = ({ children }: React.PropsWithChildren) => {
  const { isB2b, session } = useAuthContext();
  const { isAvailable, userStoreSlug } = useStoreSlug();
  const {
    total,
    total_with_coupon,
    cartproducts,
    sub_total,
    delivery_price,
    couponDetails,
    isCartFetching,
    isCartLoading,
    isUpdatingCart,
  } = useCartContext();
  const { availableCredits } = useAvailableCredits({
    totalOrder: cartproducts.length ? total_with_coupon || total : 0,
  });
  const { mainCard } = useCreditCards();
  const { applyCouponToCart, removeCouponFromCart } = useCheckoutActions();
  const { paymentMethods } = usePaymentMethods();

  const [card, setCard] = useState<CardInformation | undefined>(mainCard);
  const [useWalletCredits, setUseWalletCredits] = useState(
    checkoutInitialState.useWalletCredits
  );
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | undefined>(
    checkoutInitialState.paymentMethod
  );
  const [observations, setObservations] = useState<string | undefined>(
    checkoutInitialState.observations
  );
  const [deliveryMethodInfo, setDeliveryMethodInfo] = useState<
    DeliveryMethodInfo | undefined
  >(checkoutInitialState.deliveryMethodInfo);

  const couponDiscount = useMemo(
    () =>
      couponDetails
        ? calculateDiscount({
            cartTotal: Number(total),
            couponDetails,
            isB2b: Boolean(isB2b),
          })
        : null,
    [couponDetails, total, isB2b]
  );

  const walletCreditsUsed = useMemo(() => {
    if (useWalletCredits && availableCredits) {
      return availableCredits;
    }
    return 0;
  }, [availableCredits, useWalletCredits]);

  const amount = useMemo(() => {
    let amount = couponDiscount ? total - couponDiscount : total;
    if (walletCreditsUsed) {
      amount -= walletCreditsUsed;
    }
    return amount;
  }, [couponDiscount, total, walletCreditsUsed]);

  const disableCreateOrder =
    isCartFetching ||
    isCartLoading ||
    isUpdatingCart ||
    !cartproducts.length ||
    Boolean(userStoreSlug && !isAvailable && !isB2b) ||
    ((paymentMethod?.slug_name === PaymentMethodSlug.Card ||
      paymentMethod?.slug_name === PaymentMethodSlug.OpenPay) &&
      !card);

  const resetCheckoutState = useCallback(() => {
    setUseWalletCredits(checkoutInitialState.useWalletCredits);
    setCard(mainCard);

    const shouldResetPaymentMethod =
      paymentMethod &&
      ![PaymentMethodSlug.Clip, PaymentMethodSlug.MercadoPago].includes(
        paymentMethod?.slug_name
      );

    if (shouldResetPaymentMethod) {
      setPaymentMethod(checkoutInitialState.paymentMethod);
    }
    setDeliveryMethodInfo(checkoutInitialState.deliveryMethodInfo);
    setObservations(checkoutInitialState.observations);
  }, [mainCard, paymentMethod]);

  useEffect(() => {
    const isAuthenticated = !!session?.user.pk;
    if (!isAuthenticated) {
      resetCheckoutState();
    }
  }, [session, resetCheckoutState]);

  useEffect(() => {
    if (mainCard && !card) {
      setCard(mainCard);
    } else if (
      card &&
      mainCard &&
      isB2b === false &&
      paymentMethods &&
      !paymentMethod
    ) {
      const cardMethod = paymentMethods.find(
        (method) => method.slug_name === PaymentMethodSlug.Card
      );
      setPaymentMethod(cardMethod);
    } else if (card && !mainCard) {
      setCard(undefined);
      isB2b &&
        paymentMethod?.slug_name === PaymentMethodSlug.Card &&
        setPaymentMethod(undefined);
    }
  }, [
    mainCard,
    card,
    setCard,
    isB2b,
    paymentMethods,
    paymentMethod,
    setPaymentMethod,
  ]);

  useEffect(() => {
    if (!paymentMethod && paymentMethods?.length) {
      setPaymentMethod(paymentMethods[0]);
    }
  }, [paymentMethods]);

  return (
    <CheckoutContext.Provider
      value={{
        amount,
        card,
        setCard: setCard,
        disableCreateOrder,
        couponDetails,
        applyCouponToCart,
        removeCouponFromCart,
        summary: {
          total,
          subtotal: Number(sub_total) || 0,
          delivery: delivery_price || 0,
          couponDiscount,
          productsDiscount: 0,
          walletCreditsUsed,
        },
        paymentMethod,
        setPaymentMethod,
        deliveryMethodInfo,
        setDeliveryMethodInfo,
        resetCheckoutState,
        observations,
        setObservations,
        useWalletCredits,
        setUseWalletCredits,
      }}
    >
      {children}
    </CheckoutContext.Provider>
  );
};
